// Egy csomag (ServicePlan) szerkesztő-blokkja — a háromszintű katalógus-form újrafelhasznált egysége. A `simple`
// szolgáltatásnál a NÉV rejtve (a szolgáltatás nevéből öröklődik) → `showName={false}`; a `package` csomagoknál
// a lista minden eleme egy `<PlanEditor showName />`. A mezők a KÖZÖS form-fields-re (SelectField/TextField/…)
// épülnek, `plans.${index}.*` mező-utakon (a szülő `<Form {...form}>` FormProvider-e alatt).
//
// FELTÉTELES BLOKKOK (a legacy 1:1): fő→alkategória KASZKÁD (főkat-váltáskor az alkat auto-reset az első
// engedélyezettre); billingType `one_time` → pricingType kényszerítve `flat` + zárolva; a pricingType→priceType
// mátrix szerinti ÁRAZÓ BLOKK a KÖZÖS `PricingEditor`-ban (flat=egységár; commission=minimum; tiered=passzív+
// sávok+overflow; base_tiered=alapdíj+sávok+overflow). A usageSourceType csak használat-alapú pricingType-nál.
import { useEffect } from 'react';
import { type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BilingualField } from '@/components/shared/bilingual-field';
import { SelectField, TextField, TextareaField } from '@/components/shared/form-fields';
import { PricingEditor } from '@/components/shared/pricing-editor';
import {
  BILLING_TYPES,
  MAIN_CATEGORIES,
  PRICING_TYPES,
  type ServiceCatalogFormValues,
  showsUsageSource,
  SUBCATEGORIES_BY_MAIN,
  USAGE_SOURCE_TYPES,
  VAT_RATES,
} from '@/features/service-catalog/form';

interface PlanEditorProps {
  form: UseFormReturn<ServiceCatalogFormValues>;
  /** A plan indexe a `plans` field-array-ben. */
  index: number;
  /** Látszik-e a kétnyelvű plan-név (package → true; simple → false, mert a szolgáltatás nevéből öröklődik). */
  showName: boolean;
}

export function PlanEditor({ form, index, showName }: PlanEditorProps): JSX.Element {
  const { t } = useTranslation();
  const { watch, setValue, getValues } = form;

  const mainCategory = watch(`plans.${index}.mainCategory`);
  const billingType = watch(`plans.${index}.billingType`);
  // A `watch()` a form-REGISZTRÁCIÓ ELŐTT `undefined`-ot ad (a típus szerint nem tudná) — és a közös
  // árazó-blokk ebből dinamikus i18n-kulcsot fűz, tehát a felületen NYERS KULCS jelent meg címke helyett.
  // A `flat` a séma alapértelmezése is, tehát ez nem találgatás, hanem ugyanaz az alapérték.
  const pricingType = watch(`plans.${index}.pricingType`) ?? 'flat';

  // Fő→alkategória kaszkád: ha az aktuális alkat NEM engedélyezett az új főkategóriában → reset az elsőre.
  // (Load-safe: betöltéskor a mentett alkat érvényes a főkatjában → nincs felülírás; csak valós váltáskor resetel.)
  useEffect(() => {
    const allowed = SUBCATEGORIES_BY_MAIN[mainCategory];
    const current = getValues(`plans.${index}.subCategory`);
    if (!allowed.includes(current)) {
      setValue(`plans.${index}.subCategory`, allowed[0], {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [mainCategory, index, getValues, setValue]);

  // one_time → pricingType kényszerítve flat (a select alább zárolva is van).
  useEffect(() => {
    if (billingType === 'one_time' && pricingType !== 'flat') {
      setValue(`plans.${index}.pricingType`, 'flat', { shouldValidate: true, shouldDirty: true });
    }
  }, [billingType, pricingType, index, setValue]);

  // ── Opció-listák (enum → i18n címke) ──
  const mainCategoryOptions = MAIN_CATEGORIES.map((v) => ({
    value: v,
    label: t(`serviceCatalog.categoryLabel.${v}`),
  }));
  const subCategoryOptions = SUBCATEGORIES_BY_MAIN[mainCategory].map((v) => ({
    value: v,
    label: t(`serviceCatalog.subCategoryLabel.${v}`),
  }));
  const billingTypeOptions = BILLING_TYPES.map((v) => ({
    value: v,
    label: t(`serviceCatalog.billingTypeLabel.${v}`),
  }));
  const pricingTypeOptions = PRICING_TYPES.map((v) => ({
    value: v,
    label: t(`serviceCatalog.pricingTypeLabel.${v}`),
  }));
  const vatRateOptions = VAT_RATES.map((v) => ({
    value: v,
    label: t(`serviceCatalog.vatRateLabel.${v}`),
  }));
  const usageSourceOptions = [
    { value: '', label: t('serviceCatalog.pricing.usageSourceNone') },
    ...USAGE_SOURCE_TYPES.map((v) => ({
      value: v,
      label: t(`usageData.sourceType.${v}`),
    })),
  ];

  const oneTimeLocked = billingType === 'one_time';

  return (
    <div className="space-y-4">
      {showName && (
        <BilingualField<ServiceCatalogFormValues>
          variant="input"
          label={t('serviceCatalog.plan.name')}
          nameHu={`plans.${index}.nameHu`}
          nameEn={`plans.${index}.nameEn`}
          required
        />
      )}

      {/* Kategória-kaszkád. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SelectField<ServiceCatalogFormValues>
          name={`plans.${index}.mainCategory`}
          label={t('serviceCatalog.plan.mainCategory')}
          required
          options={mainCategoryOptions}
        />
        <SelectField<ServiceCatalogFormValues>
          name={`plans.${index}.subCategory`}
          label={t('serviceCatalog.plan.subCategory')}
          required
          options={subCategoryOptions}
        />
      </div>

      {/* Számlázás + árazás típusa (one_time → pricingType flat + zárolva). */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SelectField<ServiceCatalogFormValues>
          name={`plans.${index}.billingType`}
          label={t('serviceCatalog.plan.billingType')}
          required
          options={billingTypeOptions}
        />
        <SelectField<ServiceCatalogFormValues>
          name={`plans.${index}.pricingType`}
          label={t('serviceCatalog.plan.pricingType')}
          required
          options={pricingTypeOptions}
          disabled={oneTimeLocked}
          hint={oneTimeLocked ? t('serviceCatalog.plan.oneTimeFlatHint') : ''}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SelectField<ServiceCatalogFormValues>
          name={`plans.${index}.vatRate`}
          label={t('serviceCatalog.plan.vatRate')}
          required
          options={vatRateOptions}
        />
        {showsUsageSource(pricingType) && (
          <SelectField<ServiceCatalogFormValues>
            name={`plans.${index}.usageSourceType`}
            label={t('serviceCatalog.plan.usageSourceType')}
            options={usageSourceOptions}
          />
        )}
      </div>

      {/* ── Árazó blokk (pricingType-függő) — KÖZÖS PricingEditor ── */}
      <PricingEditor<ServiceCatalogFormValues>
        form={form}
        base={`plans.${index}`}
        pricingType={pricingType}
        t={t}
        tPrefix="serviceCatalog.pricing"
      />

      {/* Egyedi számlatétel-beállítás. */}
      <div className="space-y-4">
        <TextField<ServiceCatalogFormValues>
          name={`plans.${index}.invoiceItemName`}
          label={t('serviceCatalog.plan.invoiceItemName')}
          hint={t('serviceCatalog.plan.invoiceItemNameHint')}
          required
          autoComplete="off"
        />
        <TextareaField<ServiceCatalogFormValues>
          name={`plans.${index}.invoiceItemNote`}
          label={t('serviceCatalog.plan.invoiceItemNote')}
          rows={2}
        />
      </div>
    </div>
  );
}
